import { DateTime } from 'luxon';
export const zone = 'Europe/Berlin';
export const language = 'de-DE';
////////////////////////////////////////////////////////////////////////////////
// Excel Serial Helpers (1900 date system)
////////////////////////////////////////////////////////////////////////////////
const EXCEL_EPOCH_UTC_MS = Date.UTC(1899, 11, 30); // Excel epoch with 1900 leap-year bug accounted for
const MS_PER_DAY = 24 * 60 * 60 * 1000;
/**
 * Converts a JavaScript Date to an Excel serial (date-only) interpreted in Europe/Berlin.
 * The resulting serial encodes the Berlin calendar date and will display the same day in Excel
 * regardless of the viewer's timezone.
 */
export function toExcelSerialDate(date) {
    const dt = DateTime.fromJSDate(date, { zone });
    const y = dt.year;
    const m = dt.month; // 1-12
    const d = dt.day; // 1-31
    return Math.floor((Date.UTC(y, m - 1, d) - EXCEL_EPOCH_UTC_MS) / MS_PER_DAY);
}
/**
 * Converts a JavaScript Date to an Excel serial (date + time) interpreted in Europe/Berlin.
 * The resulting serial encodes the Berlin wall-clock time and will display the same local time in Excel
 * regardless of the viewer's timezone settings.
 */
export function toExcelSerialDateTime(date) {
    const dt = DateTime.fromJSDate(date, { zone });
    const y = dt.year;
    const m = dt.month; // 1-12
    const d = dt.day; // 1-31
    const hh = dt.hour;
    const mm = dt.minute;
    const ss = dt.second;
    const ms = dt.millisecond;
    const millisSinceEpochUtc = Date.UTC(y, m - 1, d, hh, mm, ss, ms);
    return (millisSinceEpochUtc - EXCEL_EPOCH_UTC_MS) / MS_PER_DAY;
}
export function fromIso(iso) {
    return DateTime.fromISO(iso, { zone }).toJSDate();
}
export function toIso(date) {
    return DateTime.fromJSDate(date, { zone }).toISO();
}
export function toIsoUtc(date) {
    return DateTime.fromJSDate(date, { zone: 'utc' }).toISO();
}
export function toIsoDate(date) {
    return DateTime.fromJSDate(date, { zone }).toISODate();
}
export function fromUnix(seconds) {
    return DateTime.fromSeconds(seconds, { zone }).toJSDate();
}
export function toUnix(date) {
    return DateTime.fromJSDate(date, { zone }).toUnixInteger();
}
export function now() {
    return DateTime.now().setZone(zone).toJSDate();
}
export function toHumanReadable(date, format) {
    return DateTime.fromJSDate(date, { zone }).toLocaleString({
        date_short: DateTime.DATE_SHORT,
        date_short_2digit: {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        },
        date_med: DateTime.DATE_MED,
        date_med_with_weekday: DateTime.DATE_MED_WITH_WEEKDAY,
        date_full: DateTime.DATE_FULL,
        date_huge: DateTime.DATE_HUGE,
        time_simple: DateTime.TIME_SIMPLE,
        time_with_seconds: DateTime.TIME_WITH_SECONDS,
        time_with_short_offset: DateTime.TIME_WITH_SHORT_OFFSET,
        time_with_long_offset: DateTime.TIME_WITH_LONG_OFFSET,
        time_24_simple: DateTime.TIME_24_SIMPLE,
        time_24_with_seconds: DateTime.TIME_24_WITH_SECONDS,
        time_24_with_short_offset: DateTime.TIME_24_WITH_SHORT_OFFSET,
        time_24_with_long_offset: DateTime.TIME_24_WITH_LONG_OFFSET,
        datetime_short: DateTime.DATETIME_SHORT,
        datetime_short_2digit: {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
        },
        datetime_short_with_seconds: DateTime.DATETIME_SHORT_WITH_SECONDS,
        datetime_short_with_seconds_2digit: {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        },
        datetime_med: DateTime.DATETIME_MED,
        datetime_med_with_seconds: DateTime.DATETIME_MED_WITH_SECONDS,
        datetime_med_with_weekday: DateTime.DATETIME_MED_WITH_WEEKDAY,
        datetime_full: DateTime.DATETIME_FULL,
        datetime_full_with_seconds: DateTime.DATETIME_FULL_WITH_SECONDS,
        datetime_huge: DateTime.DATETIME_HUGE,
        datetime_huge_with_seconds: DateTime.DATETIME_HUGE_WITH_SECONDS,
    }[format], {
        locale: language,
    });
}
export function datePeriodToHumanReadable(from, to) {
    const fromDate = DateTime.fromJSDate(from, { zone });
    const toDate = DateTime.fromJSDate(to, { zone });
    if (fromDate.year === toDate.year) {
        if (fromDate.month === toDate.month) {
            if (fromDate.day === toDate.day) {
                return fromDate.toLocaleString({
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                }, {
                    locale: language,
                });
            }
            return `${fromDate.toLocaleString({
                day: '2-digit',
            }, {
                locale: language,
            })} – ${toDate.toLocaleString({
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            }, {
                locale: language,
            })}`;
        }
        return `${fromDate.toLocaleString({
            month: '2-digit',
            day: '2-digit',
        }, {
            locale: language,
        })} – ${toDate.toLocaleString({
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }, {
            locale: language,
        })}`;
    }
    return `${fromDate.toLocaleString({
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }, {
        locale: language,
    })} – ${toDate.toLocaleString({
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }, {
        locale: language,
    })}`;
}
export function startOfDay(date) {
    return DateTime.fromJSDate(date, { zone }).startOf('day').toJSDate();
}
export function startOfNextDay(date) {
    return DateTime.fromJSDate(date, { zone }).startOf('day').plus({ days: 1 }).toJSDate();
}
export function endOfDay(date) {
    return DateTime.fromJSDate(date, { zone }).endOf('day').toJSDate();
}
export function startAndEndOfDay(date) {
    const dt = DateTime.fromJSDate(date, { zone });
    return {
        from: dt.startOf('day').toJSDate(),
        to: dt.endOf('day').toJSDate(),
    };
}
export function addMinutes(date, minutes) {
    return DateTime.fromJSDate(date, { zone }).plus({ minutes }).toJSDate();
}
export function addHours(date, hours) {
    return DateTime.fromJSDate(date, { zone }).plus({ hours }).toJSDate();
}
export function subHours(date, hours) {
    return DateTime.fromJSDate(date, { zone }).minus({ hours }).toJSDate();
}
export function addDays(date, days) {
    return DateTime.fromJSDate(date, { zone }).plus({ days }).toJSDate();
}
export function subDays(date, days) {
    return DateTime.fromJSDate(date, { zone }).minus({ days }).toJSDate();
}
export function addWeeks(date, weeks) {
    return DateTime.fromJSDate(date, { zone }).plus({ weeks }).toJSDate();
}
export function subWeeks(date, weeks) {
    return DateTime.fromJSDate(date, { zone }).minus({ weeks }).toJSDate();
}
export function addMonths(date, months) {
    return DateTime.fromJSDate(date, { zone }).plus({ months }).toJSDate();
}
export function subMonths(date, months) {
    return DateTime.fromJSDate(date, { zone }).minus({ months }).toJSDate();
}
export function addYears(date, years) {
    return DateTime.fromJSDate(date, { zone }).plus({ years }).toJSDate();
}
export function subYears(date, years) {
    return DateTime.fromJSDate(date, { zone }).minus({ years }).toJSDate();
}
export function getHourOfDay(date) {
    return DateTime.fromJSDate(date, { zone }).hour;
}
/**
 * Returns the ISO weekday number (1 = Monday, ..., 7 = Sunday) in Europe/Berlin.
 */
export function getWeekday(date) {
    return DateTime.fromJSDate(date, { zone }).weekday;
}
export function isValidDate(date) {
    return DateTime.fromJSDate(date, { zone }).isValid;
}
export function startOfWeek(date) {
    return DateTime.fromJSDate(date, { zone }).startOf('week').toJSDate();
}
export function endOfWeek(date) {
    return DateTime.fromJSDate(date, { zone }).endOf('week').toJSDate();
}
export function startOfMonth(date) {
    return DateTime.fromJSDate(date, { zone }).startOf('month').toJSDate();
}
export function endOfMonth(date) {
    return DateTime.fromJSDate(date, { zone }).endOf('month').toJSDate();
}
export function startOfQuarter(date) {
    return DateTime.fromJSDate(date, { zone }).startOf('quarter').toJSDate();
}
export function endOfQuarter(date) {
    return DateTime.fromJSDate(date, { zone }).endOf('quarter').toJSDate();
}
export function startOfYear(date) {
    return DateTime.fromJSDate(date, { zone }).startOf('year').toJSDate();
}
export function endOfYear(date) {
    return DateTime.fromJSDate(date, { zone }).endOf('year').toJSDate();
}
export function startOfYearByNumber(year) {
    return DateTime.fromObject({ year, month: 1, day: 1 }, { zone }).startOf('day').toJSDate();
}
export function formatDuration(durationMilliseconds) {
    const totalSeconds = Math.floor(durationMilliseconds / 1000);
    const totalMinutes = Math.floor(totalSeconds / 60);
    const totalHours = Math.floor(totalMinutes / 60);
    const days = Math.floor(totalHours / 24);
    if (days > 0) {
        const hours = totalHours % 24;
        return `${days}T ${hours}h`;
    }
    if (totalHours > 0) {
        const minutes = totalMinutes % 60;
        return `${totalHours}h ${minutes}m`;
    }
    if (totalMinutes > 0) {
        return `${totalMinutes}m`;
    }
    return `${totalSeconds}s`;
}
export function formatDateTime(date, format) {
    return DateTime.fromJSDate(date, { zone }).toFormat(format);
}
